<template>
  <li v-show="canShowCategoryLink">
    <div class="lg:flex lg:justify-between">
      <div class="flex flex-col lg:flex-row lg:items-center">
        <div class="flex items-center justify-between">
          <GoBack
            class="mr-5"
            :back-button-text="backButtonText"
            @go-back="onGoBack()"
          />
          <ClientMobileOnly>
            <CloseNavigation />
          </ClientMobileOnly>
        </div>
        <ClientDesktopOnly>
          <NavigationLink
            v-bind="shopAllButtonProps"
          />
        </ClientDesktopOnly>
      </div>
      <ClientDesktopOnly>
        <div
          class="flex gap-1 items-center"
        >
          <NavigationLink
            :is-primary="true"
            class="mr-6"
            v-bind="bestsellerButtonProps"
          />
          <NavigationLink
            class="text-EXPONDO-red underline"
            v-bind="onSaleButtonProps"
          />
        </div>
      </ClientDesktopOnly>
    </div>

    <NavCategoryList
      v-if="!currentSecondLevelCategoryName"
      class="list-none"
    >
      <NavCategoryListItem
        v-for="secondLevelCategory in secondLevelCategories"
        :key="secondLevelCategory.id || secondLevelCategory.slug"
        :category="secondLevelCategory"
        :data-testid="CATEGORY_TEST_IDS.SUB_CATEGORY_BUTTON"
      />
    </NavCategoryList>

    <ClientMobileOnly>
      <div class="mt-12 flex flex-col gap-3">
        <NavigationLink
          class="font-bold text-EXPONDO-black"
          v-bind="bestsellerButtonProps"
        />
        <NavigationLink
          class="font-bold text-EXPONDO-red"
          v-bind="onSaleButtonProps"
        />
        <NavigationLink
          v-bind="shopAllButtonProps"
        />
      </div>
    </ClientMobileOnly>
  </li>
</template>

<script lang="ts">

import once from 'lodash/once.js';
import type { Category } from '~~/types/commercetools';

import { LINK_TYPE } from '~~/types/components/Link/LinkType';
import { sortCategories } from '~~/helpers/category/sortCategories';
import { createCategoryBestsellersLink, createCategoryOnSaleLink } from '~~/helpers/navigation/createNavLinks';
import { removeEmptyCategories } from '~~/helpers/category/categoriesTree';
import { buildCategoryPageUrl } from '~~/helpers/category/buildCategoryPageUrl';
import { CATEGORY_TEST_IDS } from '~~/constants/categoriesTestIds';

export default defineComponent({
  name: 'NavFirstLevelCategory',

  props: {
    category: {
      type: Object as PropType<Category>,
      required: true,
    },
  },
  setup(props) {

    const {
      currentSecondLevelCategoryName,
      currentFirstLevelCategorySlug,
      currentSecondLevelCategorySlug,
      setFirstLevelCategory,
    } = useNavigationState();
    const i18n = useI18n();
    const { languageAndCountry, countryCode } = useLocalization();

    const categoryTitle = computed(() => currentSecondLevelCategoryName.value || props.category.name);
    const categorySlug = computed(() => currentSecondLevelCategorySlug.value || props.category.slug);
    const targetCategoryUrl = computed(() => buildCategoryPageUrl({
      category: categorySlug.value,
      countryCode: countryCode.value,
    }));
    const canShowCategoryLink = computed(() => props.category.slug === currentFirstLevelCategorySlug.value);
    const secondLevelCategories = ref(props.category.children);
    const sortCategoriesOnce = once(() => {

      const onlyCategoriesWithProducts = removeEmptyCategories(secondLevelCategories.value, languageAndCountry.value);
      sortCategories(onlyCategoriesWithProducts);
      secondLevelCategories.value = onlyCategoriesWithProducts;

    });
    watch(canShowCategoryLink, () => {

      sortCategoriesOnce();

    });
    onMounted(() => {

      if (canShowCategoryLink.value) {

        sortCategoriesOnce();

      }

    });

    const onGoBack = () => {

      if (!currentSecondLevelCategoryName.value) {

        setFirstLevelCategory();

      }

    };

    const backButtonText: ComputedRef<string> = computed(() => {

      if (!categoryTitle.value) {

        return i18n.t('Categories');

      } else {

        return categoryTitle.value;

      }

    });

    const categoryOnSaleLink = createCategoryOnSaleLink(props.category.slug, countryCode.value);
    const categoryBestsellersLink = createCategoryBestsellersLink(props.category.slug, countryCode.value);

    const bestsellerButtonProps = {
      text: i18n.t('bestseller'),
      url: categoryBestsellersLink,
    };
    const shopAllButtonProps = {
      'is-primary': true,
      text: i18n.t('Shop All'),
      url: targetCategoryUrl.value,
      'data-testid': 'showAllProducts',
    };
    const onSaleButtonProps = {
      text: i18n.t('on-sale'),
      url: categoryOnSaleLink,
    };

    return {
      currentSecondLevelCategoryName,
      CATEGORY_TEST_IDS,
      LINK_TYPE,
      secondLevelCategories,
      onGoBack,
      backButtonText,
      categoryOnSaleLink,
      categoryBestsellersLink,
      canShowCategoryLink,
      bestsellerButtonProps,
      shopAllButtonProps,
      onSaleButtonProps,
    };

  },
});

</script>
